import { Form, FormikProvider, useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { Modal, ModalBody, ModalHeader } from 'reactstrap'
import * as Yup from 'yup'
import { formatAgentOptions } from '../../../api/agents/formatters'
import agents from '../../../api/agents/hooks'
import notes from '../../../api/notes/hooks'
import { NoteForm } from '../../../api/notes/types'
import { MODAL_TYPES } from '../../../common/constants'
import { noteInputs } from '../../../common/forms/notes/inputs'
import { noteValidation } from '../../../common/forms/notes/validation'
import { hideModal } from '../../../store/modal'
import { SaveButton } from '../../Common/Buttons'
import RecursiveForm from '../../RecursiveForm'

interface NotesModalProps {
    isOpen: boolean
    notesModal: {
        noteEntity: string
        entityId: string
        note?: NoteForm
    }
}

const NotesModal = (props: NotesModalProps) => {
    const dispatch = useDispatch()
    const { t } = useTranslation()

    const { isOpen, notesModal } = props
    const { noteEntity, entityId, note } = notesModal

    const inputs = noteInputs()
    const { initialValues, validationSchema } = noteValidation(note)

    const { data: agentsData } = agents.GetAgents.useQuery()
    const { mutate: createNote, isPending: isCreating } = notes.CreateUpdateNote.useMutation(false)
    const { mutate: updateNote, isPending: isUpdating } = notes.CreateUpdateNote.useMutation(true)

    const selectOptions = {
        agent_id: formatAgentOptions(agentsData),
    }

    const loading = isUpdating || isCreating

    const formik = useFormik({
        enableReinitialize: true,
        initialValues: initialValues ?? {},
        validationSchema: validationSchema ?? Yup.object({}),
        onSubmit: (values) => {
            const submitValues = {
                ...values,
                noteEntity,
                [`${noteEntity}_id`]: entityId,
            } as NoteForm

            if (!note?.id) {
                createNote({ ...submitValues })
            } else {
                updateNote({ id: note.id, ...submitValues })
            }
        },
    })

    const handleClose = () => dispatch(hideModal({ modalType: MODAL_TYPES.NOTES_MODAL }))

    return (
        <Modal isOpen={isOpen}>
            <ModalHeader toggle={handleClose} tag="h5" className="p-3  modal-title">
                {note?.id ? t('modal.notes.edit') : t('app.common.addNote')}
            </ModalHeader>
            <ModalBody>
                {formik && (
                    <FormikProvider value={formik}>
                        <Form onSubmit={formik.handleSubmit}>
                            <RecursiveForm
                                inputs={inputs?.mainForm}
                                formik={formik}
                                selectOptions={selectOptions}
                                selectOptionsLoading={undefined}
                                config={undefined}
                                subform={undefined}
                                inputComponents={undefined}
                                fieldArrayDeleteComponent={undefined}
                            />

                            <div className="hstack gap-2 justify-content-end">
                                <SaveButton loading={loading} />
                            </div>
                        </Form>
                    </FormikProvider>
                )}
            </ModalBody>
        </Modal>
    )
}

export default NotesModal
